import {
  CATEGORIAS_DE_DOCUMENTO,
  ETIQUETA_CATEGORIA_DOCUMENTO,
  ETIQUETA_CATEGORIA_DOCUMENTO_EN,
  EXTENSIONES_DEL_EXPEDIENTE,
  type CategoriaDeDocumento,
  type ExportacionDetalle,
} from '@cnc/contracts';
import {useRef, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {
  rutaDeDocumentoDelExpediente,
  rutaDelZipDelExpediente,
  useBorrarDelExpediente,
  useSubirAlExpediente,
} from '../api/documentos';
import {et, tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {AYUDA_EXPEDIENTE} from './ayuda-exportacion';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';
import {seVeEnLaApp} from '../utilidades/vista-previa';
import {VistaPrevia} from './vista-previa';
import {PistaDeSoltar, ZonaParaSoltar} from './zona-para-soltar';

const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';

// Expediente documental de la exportacion, en el servidor: quien tiene
// permiso de edicion sube y borra; todos consultan y descargan. Con la carpeta
// de Dropbox a la vista (decision 0092) muestra solo lo que no esta en ella.
export function Expediente({
  codigo,
  documentos,
  soloEnLaApp = false,
  total,
}: {
  codigo: string;
  documentos: ExportacionDetalle['documentos'];
  soloEnLaApp?: boolean;
  total?: number;
}) {
  const subir = useSubirAlExpediente(codigo);
  const borrar = useBorrarDelExpediente(codigo);
  const etiquetas = et(ETIQUETA_CATEGORIA_DOCUMENTO, ETIQUETA_CATEGORIA_DOCUMENTO_EN);
  return (
    <VistaDeExpediente
      documentos={documentos}
      soloEnLaApp={soloEnLaApp}
      total={total}
      categorias={CATEGORIAS_DE_DOCUMENTO.map(c => ({valor: c, etiqueta: etiquetas[c]}))}
      subiendo={subir.isPending}
      borrando={borrar.isPending}
      subir={(archivo, categoria) => subir.mutateAsync({archivo, categoria: categoria as CategoriaDeDocumento})}
      borrar={id => borrar.mutateAsync(id)}
      rutaDelDocumento={rutaDeDocumentoDelExpediente}
      rutaDeLaVista={rutaDeVistaDelExpediente}
      rutaDelZip={rutaDelZipDelExpediente(codigo)}
    />
  );
}

type DocumentoDeLaVista = {
  id: number;
  nombre: string;
  categoriaEtiqueta: string;
  tamanoBytes: number;
  subidoEl: string;
};

// La vista del expediente, de concentrados o de doré (decision 0095).
export function VistaDeExpediente({
  documentos,
  soloEnLaApp = false,
  total,
  categorias,
  subiendo,
  borrando,
  subir,
  borrar,
  rutaDelDocumento,
  rutaDeLaVista,
  rutaDelZip,
}: {
  documentos: DocumentoDeLaVista[];
  soloEnLaApp?: boolean;
  total?: number;
  categorias: Array<{valor: string; etiqueta: string}>;
  subiendo: boolean;
  borrando: boolean;
  subir: (archivo: File, categoria: string) => Promise<unknown>;
  borrar: (id: number) => Promise<unknown>;
  rutaDelDocumento: (id: number) => string;
  rutaDeLaVista: (id: number) => string;
  rutaDelZip: string;
}) {
  const permisos = usePermisos();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [categoria, setCategoria] = useState('other');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [viendo, setViendo] = useState<DocumentoDeLaVista | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const enviar = async () => {
    if (!archivo) return;
    setMensaje(null);
    try {
      await subir(archivo, categoria);
      setArchivo(null);
      if (entrada.current) entrada.current.value = '';
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi ? error.message : tr('No se pudo subir el archivo.', 'Could not upload the file.'),
      );
    }
  };

  const quitar = async (id: number, nombre: string) => {
    if (
      !window.confirm(
        tr(
          `¿Borrar "${nombre}" del expediente? Quedará registrado en la bitácora.`,
          `Delete "${nombre}" from the document file? It will be recorded in the audit log.`,
        ),
      )
    )
      return;
    setMensaje(null);
    try {
      await borrar(id);
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi ? error.message : tr('No se pudo borrar el archivo.', 'Could not delete the file.'),
      );
    }
  };

  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-medium">
          {soloEnLaApp
            ? tr(
                `Solo en el expediente de la app (${numero(documentos.length)} de ${numero(total ?? documentos.length)})`,
                `Only in the app’s export file (${numero(documentos.length)} of ${numero(total ?? documentos.length)})`,
              )
            : `${tr('Expediente documental', 'Document file')} (${numero(documentos.length)})`}
        </h2>
        {documentos.length > 0 && !soloEnLaApp && (
          <a href={rutaDelZip} className="text-xs text-verde hover:underline" download>
            ↓ {tr('Descargar todo (ZIP)', 'Download everything (ZIP)')}
          </a>
        )}
      </div>
      <p className="mt-1 text-xs text-apagado">
        {soloEnLaApp
          ? tr(
              'Documentos del expediente que no están en la carpeta de Dropbox. Lo que subas aquí, con su categoría, queda también en Dropbox.',
              'Export file documents that are not in the Dropbox folder. What you upload here, with its category, also stays in Dropbox.',
            )
          : tr(
              'Facturas, BL, certificados y demás archivos de la exportación, guardados en el servidor para todo el equipo.',
              'Invoices, BL, certificates and other files of the export, stored on the server for the whole team.',
            )}
      </p>

      {permisos.editar && (
        <ZonaParaSoltar className="mt-3">
          <form
            className="grid gap-2 rounded-md border border-linea bg-papel p-3 sm:grid-cols-[1fr_auto_auto] sm:items-end"
            onSubmit={e => {
              e.preventDefault();
              void enviar();
            }}
          >
            <label className="block text-xs text-apagado">
              {tr('Archivo (PDF, Excel, Word, imagen o ZIP)', 'File (PDF, Excel, Word, image or ZIP)')}
              <NotaDeAyuda texto={AYUDA_EXPEDIENTE.archivo} dentroDeEtiqueta />
              <input
                ref={entrada}
                type="file"
                accept={EXTENSIONES_DEL_EXPEDIENTE.join(',')}
                onChange={e => setArchivo(e.target.files?.[0] ?? null)}
                className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-linea file:bg-white file:px-3 file:py-1.5 file:text-sm"
              />
              {!archivo && <PistaDeSoltar />}
            </label>
            <label className="block text-xs text-apagado">
              {tr('Categoría', 'Category')}
              <NotaDeAyuda titulo={tr('Categoría', 'Category')} texto={AYUDA_EXPEDIENTE.categoria} dentroDeEtiqueta />
              <select
                value={categoria}
                onChange={e => setCategoria(e.target.value)}
                className="mt-1 w-full rounded-md border border-linea bg-white px-2 py-1.5 text-sm text-tinta"
                aria-label={tr('Categoría del documento', 'Document category')}
              >
                {categorias.map(c => (
                  <option key={c.valor} value={c.valor}>
                    {c.etiqueta}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className={boton} disabled={!archivo || subiendo}>
              {subiendo ? tr('Subiendo…', 'Uploading…') : tr('Subir al expediente', 'Upload to the document file')}
            </button>
          </form>
        </ZonaParaSoltar>
      )}
      {mensaje && <p className="mt-2 text-sm text-alerta">{mensaje}</p>}

      <div className="mt-3">
        {documentos.length === 0 ? (
          <p className="text-sm text-apagado">
            {soloEnLaApp
              ? tr('Todo el expediente está también en Dropbox.', 'The whole export file is also in Dropbox.')
              : tr('Aún no hay documentos en el expediente.', 'There are no documents in the document file yet.')}
          </p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {documentos.map(d => (
              <li key={d.id} className="flex items-center gap-3 rounded-md border border-linea px-3 py-1.5">
                <span className="rounded bg-papel px-1.5 py-0.5 text-[10px] font-semibold text-apagado">
                  {extension(d.nombre)}
                </span>
                <a
                  href={rutaDelDocumento(d.id)}
                  className="flex-1 truncate text-verde hover:underline"
                  title={d.nombre}
                  download
                >
                  {d.nombre}
                </a>
                <span className="text-xs text-apagado">
                  {d.categoriaEtiqueta} · {tamano(d.tamanoBytes)} · {fecha(d.subidoEl.slice(0, 10))}
                </span>
                {seVeEnLaApp(d.nombre) && (
                  <button
                    type="button"
                    onClick={() => setViendo(viendo?.id === d.id ? null : d)}
                    aria-label={tr(`Ver ${d.nombre}`, `View ${d.nombre}`)}
                    className="rounded-md border border-linea px-2 py-0.5 text-xs hover:bg-papel"
                  >
                    {tr('👁 Ver', '👁 View')}
                  </button>
                )}
                {permisos.editar && (
                  <button
                    type="button"
                    onClick={() => void quitar(d.id, d.nombre)}
                    disabled={borrando}
                    className="text-xs text-apagado hover:text-alerta"
                    aria-label={tr(`Borrar ${d.nombre}`, `Delete ${d.nombre}`)}
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {viendo && (
        <VistaPrevia
          src={rutaDeLaVista(viendo.id)}
          nombre={viendo.nombre}
          abrir={{href: rutaDelDocumento(viendo.id), texto: tr('↓ Descargar', '↓ Download')}}
          onCerrar={() => setViendo(null)}
        />
      )}
    </div>
  );
}

const rutaDeVistaDelExpediente = (id: number) => `/api/expediente/${id}/vista`;

const extension = (nombre: string) => (nombre.split('.').pop() ?? 'FILE').slice(0, 4).toUpperCase() || 'FILE';
const tamano = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${numero(bytes / 1024 / 1024, 2)} MB` : `${numero(Math.max(1, Math.round(bytes / 1024)))} KB`;
